import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion /
// mood_tracker). askBrain sends the message + this app's declared action catalog to the generic
// fleet chat brain (POST /api/apps/oriora_stash/chat) — ONE billed call returns ONE registered
// action + a reply; the app executes it locally so the catalog stays on-device. The regex
// intent-router below is the FREE FALLBACK (offline / signed-out / out of funds) — chat never
// goes dead. Original prototype notes:
//   • "add <name> [, in <location>] [, for <season>] [, category X] [, qty N]" → add_item   (local, free)
//   • "mark <name> used" / "used <name>"                                       → mark_used  (local, free)
//   • "move <name> to <location>" / "put <name> in <location>"                 → edit location (local, free)
//   • "set <name> season <season>" / "set <name> category <c>" / "<name> qty N"→ edit item  (local, free)
//   • "show <term>" / "never used" / "find X"                                  → filter     (local, free)
//   • "recap <season>" / "what do I have for <season>"                         → recapSeason (billed)
//   • "ask <q>" / any question ("where are my boots?")                         → askStash    (billed)
//   • "summarize / overview"                                                   → local stats (local, free)
//   • anything else / styling-shopping advice                                  → none + a nudge / the fence
//
// STASH FENCE (can-only-describe-owned-items ceiling — HARD, MANDATORY pre-LLM on BOTH paths).
// This app describes what the user owns and where; it NEVER gives styling, shopping, or
// keep-or-toss advice. crossesFence() runs on the raw message BEFORE serverChat is ever called —
// an advice-shaped ask is refused for FREE, so it never reaches the (billed) routing call either,
// not just the (billed) recap/ask calls one step later (which keep their own fence too, defence
// in depth — mirrors brain.ts::crossesFence). This mirrors the pre-graduation design, which ran
// the fence first on every message before any branch.

import { store, todayLocal, normalizeToken, titleCase, summarize, summaryCopy, type Item, type NewItem, type ItemPatch } from "../store";
import { BrainError, crossesFence, FENCE_REDIRECT, brainCall } from "../brain";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string; // conversational preface; executeAction supplies the result line
  action: ChatAction;
}
// The brain can return MULTIPLE actions for one message (e.g. "add 3 things"), executed in order.
export interface BrainResult {
  reply: string;
  actions: ChatAction[];
}

const HELP =
  "I can add items, mark them used, move or re-tag them, search your catalog, summarize it, recap a season, ask about it, and delete — just tell me. Try:\n" +
  '• "add string lights, in Attic, for christmas, qty 3"\n' +
  '• "mark string lights used" · "move string lights to Garage"\n' +
  '• "show christmas" · "never used" · "summarize"\n' +
  '• "recap christmas" · "where are my winter boots?"';

const none = (message: string): BrainResponse => ({ reply: "", action: { kind: "none", message } });

export async function findByName(term: string, filter?: (i: Item) => boolean): Promise<Item[]> {
  const q = normalizeToken(term).replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listItems({ limit: 50000 });
  const scoped = filter ? all.filter(filter) : all;
  // Forgiving name match first (misspelling / dropped word / word order — fleet fuzzy standard).
  const byName = findNamedMatches(scoped, term);
  if (byName.length) return byName;
  // Fall back to Stash's own fields (where it's stored / what occasion).
  return scoped.filter((i) => i.location.toLowerCase().includes(q) || i.occasion.toLowerCase().includes(q));
}

/** "Couldn't find X" enriched with the closest item names ("did you mean …?"). */
async function notFoundFor(term: string, filter?: (i: Item) => boolean): Promise<string> {
  const all = await store.listItems({ limit: 50000 });
  const scoped = filter ? all.filter(filter) : all;
  return notFoundMessage(scoped, term);
}

function pickList(rows: Item[]): string {
  return rows.slice(0, 5).map((i) => `• ${i.name}${i.location ? ` (${i.location})` : ""}`).join("\n");
}

async function resolveSeason(term: string): Promise<string> {
  const q = normalizeToken(term).replace(/[.?!]+$/, "");
  const { seasons } = await store.listFacets();
  const exact = seasons.find((s) => s.toLowerCase() === q);
  if (exact) return exact;
  const partial = seasons.find((s) => s.toLowerCase().includes(q) || q.includes(s.toLowerCase()));
  return partial ?? q; // fall back to the raw token; recapSeason guards empty seasons
}

function parseAdd(s: string): NewItem {
  let rest = s.trim();
  const out: NewItem = { name: "" };
  const grab = (re: RegExp): string | undefined => {
    const m = rest.match(re);
    if (m) { rest = rest.replace(m[0], " ").trim(); return m[1].trim(); }
    return undefined;
  };
  const loc = grab(/\bin\s+(?:the\s+)?([^,]+)/i);
  if (loc) out.location = loc;
  const season = grab(/\b(?:for|season)\s+([^,]+)/i);
  if (season) out.season = normalizeToken(season);
  const category = grab(/\b(?:category|cat|type)\s+([^,]+)/i);
  if (category) out.category = normalizeToken(category);
  const occ = grab(/\boccasion\s+([^,]+)/i);
  if (occ) out.occasion = occ;
  const qty = grab(/\b(?:qty|quantity|count)\s*(\d+)\b/i) ?? grab(/\b(\d+)\s*(?:units?|pcs?|pieces?)\b/i);
  if (qty) out.quantity = Math.max(1, parseInt(qty, 10));
  out.name = rest.split(",")[0].trim().replace(/\s{2,}/g, " ");
  return out;
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResult> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }

  // 🔴 THE FENCE RUNS PRE-LLM, HERE — before any billed call, exactly as this file's header
  // states and as 29 sibling apps do it. It was absent between 2026-07-09 (commit 90da0f8, a
  // carry-forward that brought in a sibling's "no client hard-fence" variant) and 2026-08-28,
  // which left the file contradicting its own header AND `fence-battery.mjs`: "Should I buy a
  // new winter coat?" — verbatim from this app's must-refuse battery — reached the network and
  // rendered no refusal (reproduced live, `probe-leads.mjs` LEAD 4). Prompting the server with a
  // `scope` string is not a fence: §5c already proved an explicit instruction gets ignored by the
  // very next generation, and it costs the customer money to find out. In code it cannot be
  // talked around and it is free. routeMessage() keeps the same check so losing the network
  // cannot open the gate either.
  if (crossesFence(text)) return { reply: "", actions: [{ kind: "none", message: FENCE_REDIRECT }] };

  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router still answers (single action).
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (item name/season text → id) locally, because the
// data lives here.

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.stash.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_item", description: "Catalog a new seasonal item.", params: { name: "REQUIRED — the item's name", location: "optional storage location, e.g. Attic", season: "optional season/occasion token, e.g. christmas, summer", category: "optional category, e.g. decor, clothing", occasion: "optional free-text occasion tag", quantity: "optional integer quantity owned" } },
  { name: "edit_item", description: "Change an existing item's name (rename), location, season, category, quantity, occasion, or notes. Give at least one field to change. Use for 'rename X to Y', 'call the lights the fairy lights', 'add a note to X'.", params: { target: "REQUIRED — which item, name as the user wrote it", name: "new name, only if renaming it", location: "new storage location, only if changing it", season: "new season token, only if changing it", category: "new category, only if changing it", quantity: "new integer quantity, only if changing it", occasion: "new free-text occasion tag, only if changing it", notes: "new free-text note, only if changing it" } },
  { name: "mark_used", description: "Mark an item as used/worn/taken out today.", params: { target: "REQUIRED — which item, name as the user wrote it" } },
  { name: "delete_item", description: "Delete one catalog item (the app asks the user to confirm first).", params: { target: "REQUIRED — which item, name as the user wrote it" } },
  { name: "filter", description: "Search / list the catalog by season, category, location, occasion, free text, or use status.", params: { season: "optional season to filter by", category: "optional category to filter by", location: "optional location to filter by", occasion: "optional occasion to filter by", text: "optional free text to match against name/season/category/location/occasion", used: 'optional: "never" for never-used items, "used" for already-used items' } },
  { name: "recap", description: "Narrate what the user owns for ONE season — counts, locations, never-used items. Observational only, never advice.", params: { season: "REQUIRED — which season, as the user wrote it" } },
  { name: "ask", description: "Answer a free-form question about the catalog (what/where/how many). Observational only — never styling or shopping advice.", params: { question: "REQUIRED — the user's question, verbatim" } },
  { name: "summarize", description: "Show a quick on-device summary of the whole catalog.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (item photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a note or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a note.", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on (e.g. 'always back up to my GitHub').", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerChatItem { action?: string; name?: string; params?: Record<string, unknown> }
interface ServerChat { action?: string; params?: Record<string, unknown>; reply?: string; actions?: ServerChatItem[] }

// The app's fence sent to the brain as `scope` — assist mode helps proactively WITHIN the domain
// while keeping subjective asks honestly framed (three-tier), never a flat refusal.
const STASH_SCOPE =
  "Oriora Stash is a personal possessions inventory — what you own, where it's stored, how many, and when it was last used. WITHIN that domain, help proactively: add items (even several at once), edit / move / re-tag, mark used, search, recap a season, summarize, answer questions about the catalog, and save a note or the whole conversation to the customer's OWN cloud. If the user asks something subjective (what to keep, a decluttering approach), give a brief FRAMED take (\"I'm an AI, based on common approaches many people…\") and steer to their own preference — don't flatly refuse. HARD limits: no medical / legal / financial / tax advice.";

async function buildContext(): Promise<string> {
  const items = await store.listItems({ limit: 50000 });
  const s = summarize(items);
  const parts = [summaryCopy(s)];
  try {
    const facets = await store.listFacets();
    if (facets.seasons.length) parts.push(`Seasons: ${facets.seasons.slice(0, 20).join(", ")}.`);
    if (facets.categories.length) parts.push(`Categories: ${facets.categories.slice(0, 20).join(", ")}.`);
    if (facets.locations.length) parts.push(`Locations: ${facets.locations.slice(0, 20).join(", ")}.`);
  } catch { /* context is optional */ }
  return parts.join(" ");
}

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResult> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: STASH_SCOPE, assist: true }, onChunk);
  const say = asStr(r.reply) ?? "";
  // The brain may return an ARRAY of actions (several items in one message → one each). Map each to a
  // resolved on-device ChatAction. Back-compat: a single {action, params} still works.
  const list: ServerChatItem[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);
  const mapped: ChatAction[] = [];
  for (const it of list) {
    const single = await toAction({ action: it.action ?? it.name ?? "", params: it.params, reply: "" });
    mapped.push(single.action);
  }
  if (mapped.length === 0) return { reply: say || HELP, actions: [{ kind: "none" }] };
  return { reply: say, actions: mapped };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asQty = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? Math.max(1, Math.round(n)) : undefined;
};

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_item": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What\'s the item called? Try "add string lights in Attic for christmas".');
      const input: NewItem = { name };
      const location = asStr(p.location);
      if (location) input.location = location;
      const season = asStr(p.season);
      if (season) input.season = normalizeToken(season);
      const category = asStr(p.category);
      if (category) input.category = normalizeToken(category);
      const occasion = asStr(p.occasion);
      if (occasion) input.occasion = occasion;
      const quantity = asQty(p.quantity);
      if (quantity) input.quantity = quantity;
      return { reply: say, action: { kind: "add_item", input } };
    }
    case "edit_item": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item should I update?");
      const rows = await findByName(target);
      if (rows.length === 0) return none(await notFoundFor(target));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      const patch: ItemPatch = {};
      const bits: string[] = [];
      const newName = asStr(p.name);
      if (newName) { patch.name = newName; bits.push(`renamed → ${newName}`); }
      const location = asStr(p.location);
      if (location) { patch.location = location; bits.push(`→ ${location}`); }
      const season = asStr(p.season);
      if (season) { const norm = normalizeToken(season); patch.season = norm; bits.push(`season → ${titleCase(norm)}`); }
      const category = asStr(p.category);
      if (category) { const norm = normalizeToken(category); patch.category = norm; bits.push(`category → ${titleCase(norm)}`); }
      const quantity = asQty(p.quantity);
      if (quantity) { patch.quantity = quantity; bits.push(`×${quantity}`); }
      const occasion = asStr(p.occasion);
      if (occasion) { patch.occasion = occasion; bits.push(`occasion → ${occasion}`); }
      const notes = asStr(p.notes);
      if (notes) { patch.notes = notes; bits.push("note updated"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, location, season, category, quantity, occasion, or notes?");
      return { reply: say, action: { kind: "edit_item", id: rows[0].id, patch, label: `${rows[0].name} ${bits.join(" ")}`.trim() } };
    }
    case "mark_used": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item did you use?");
      const rows = await findByName(target);
      if (rows.length === 0) return none(await notFoundFor(target));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "mark_used", id: rows[0].id, label: rows[0].name } };
    }
    case "delete_item": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item do you want to delete?");
      const rows = await findByName(target);
      if (rows.length === 0) return none(await notFoundFor(target));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${target}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_item", id: rows[0].id, label: rows[0].name } };
    }
    case "filter": {
      const season = asStr(p.season)?.toLowerCase();
      const category = asStr(p.category)?.toLowerCase();
      const location = asStr(p.location)?.toLowerCase();
      const occasion = asStr(p.occasion)?.toLowerCase();
      const text = asStr(p.text)?.toLowerCase();
      const used = asStr(p.used)?.toLowerCase();
      const pred = (i: Item): boolean => {
        if (used === "never" && i.last_used_on) return false;
        if (used === "used" && !i.last_used_on) return false;
        if (season && !i.season.toLowerCase().includes(season)) return false;
        if (category && !i.category.toLowerCase().includes(category)) return false;
        if (location && !i.location.toLowerCase().includes(location)) return false;
        if (occasion && !i.occasion.toLowerCase().includes(occasion)) return false;
        if (text) {
          const hay = `${i.name} ${i.season} ${i.category} ${i.location} ${i.occasion}`.toLowerCase();
          if (!hay.includes(text)) return false;
        }
        return true;
      };
      return { reply: "", action: { kind: "filter", pred, label: say || undefined } };
    }
    case "recap": {
      const seasonTerm = asStr(p.season);
      if (!seasonTerm) return none(say || 'Which season? Try "recap christmas".');
      const season = await resolveSeason(seasonTerm);
      return { reply: say, action: { kind: "recap", season, label: titleCase(season) } };
    }
    case "ask": {
      const question = asStr(p.question);
      if (!question) return none(say || "What do you want to ask about your catalog?");
      return { reply: say, action: { kind: "ask", question } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 0) FENCE — never let the chat give styling / shopping / keep-or-toss advice (mirrors brain.ts).
  if (crossesFence(t)) return { reply: "", action: { kind: "none", message: FENCE_REDIRECT } };

  // 1) EXPLICIT ASK — "ask <question>" → the live NL query (billed).
  let m = t.match(/^ask\s+(.+)$/i);
  if (m) return { reply: "", action: { kind: "ask", question: m[1].trim() } };

  // 2) SUMMARIZE — local catalog stats (free).
  if (/\b(summar(?:y|ise|ize)|overview)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 3) RECAP <season> — the live season recap (billed).
  m = t.match(/^recap\s+(?:for\s+|of\s+)?(.+)$/i)
    || t.match(/^(?:what do i have|what have i got|whats?)\s+for\s+(.+)$/i);
  if (m) {
    const season = await resolveSeason(m[1]);
    return { reply: "", action: { kind: "recap", season, label: titleCase(season) } };
  }

  // 4) DELETE — resolve locally; the UI asks for confirmation before executing.
  m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:item\s+)?(.+)$/i);
  if (m) {
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "delete_item", id: rows[0].id, label: rows[0].name } };
  }

  // 5) MARK USED — stamp the item as used today.
  m = t.match(/^(?:mark|set)\s+(.+?)\s+(?:as\s+)?used\s*(?:today)?\s*$/i)
    || t.match(/^used\s+(.+)$/i)
    || t.match(/^(.+?)\s+(?:was|is)\s+used\s*(?:today)?\s*$/i);
  if (m) {
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "mark_used", id: rows[0].id, label: rows[0].name } };
  }

  // 6) MOVE / STORE — set the item's location.
  m = t.match(/^(?:move|relocate)\s+(.+?)\s+to\s+(.+)$/i)
    || t.match(/^(?:put|store|stash)\s+(.+?)\s+in\s+(?:the\s+)?(.+)$/i);
  if (m) {
    const location = m[2].trim().replace(/[.?!]+$/, "");
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { location }, label: `${rows[0].name} → ${location}` } };
  }

  // 7) SET SEASON — "set <name> season <season>" / "tag <name> for <season>".
  m = t.match(/^(?:set|tag)\s+(.+?)\s+(?:season|for)\s+(.+)$/i);
  if (m) {
    const season = normalizeToken(m[2]);
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { season }, label: `${rows[0].name} → ${titleCase(season)}` } };
  }

  // 8) SET CATEGORY — "set <name> category <c>".
  m = t.match(/^(?:set|tag)\s+(.+?)\s+(?:category|type)\s+(.+)$/i);
  if (m) {
    const category = normalizeToken(m[2]);
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { category }, label: `${rows[0].name} → ${titleCase(category)}` } };
  }

  // 9) SET QUANTITY — "set <name> quantity N" / "<name> qty N".
  m = t.match(/^(?:set\s+)?(.+?)\s+(?:qty|quantity|count)\s+(\d+)\s*$/i);
  if (m) {
    const quantity = Math.max(1, parseInt(m[2], 10));
    const rows = await findByName(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an item matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { quantity }, label: `${rows[0].name} → ×${quantity}` } };
  }

  // 10) ADD — light local parse (no server call).
  m = t.match(/^(?:add|log|new|track|catalog)\s+(?:a\s+|an\s+)?(?:item\s+)?(.+)$/i);
  if (m) {
    const input = parseAdd(m[1]);
    if (!input.name) return { reply: "", action: { kind: "none", message: 'What\'s the item called? Try "add string lights in Attic for christmas".' } };
    return { reply: "", action: { kind: "add_item", input } };
  }

  // 11) FILTER / SEARCH — local predicate over the catalog (free).
  const filter = buildFilter(t, low);
  if (filter) return { reply: "", action: { kind: "filter", pred: filter.pred, label: filter.label } };

  // 12) QUESTION-SHAPED → the live NL query (billed). Fence already cleared above.
  if (/\?\s*$/.test(t) || /^(?:where|what|which|when|how many|do i have|is there)\b/i.test(low)) {
    return { reply: "", action: { kind: "ask", question: t } };
  }

  // 13) Unclear → a short nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

function buildFilter(t: string, low: string): { pred: (i: Item) => boolean; label: string } | null {
  if (/\b(never\s*used|unused|not\s+used)\b/i.test(low)) return { pred: (i) => !i.last_used_on, label: "never used" };
  if (/\b(already\s+used|recently\s+used|used\s+items)\b/i.test(low)) return { pred: (i) => !!i.last_used_on, label: "used" };
  // "show/find <term>" → match by season, category, name, location, or occasion.
  const m = t.match(/^(?:show|find|list|search)\s+(?:me\s+)?(?:all\s+)?(?:my\s+)?(.+)$/i);
  if (m) {
    const q = normalizeToken(m[1]).replace(/[.?!]+$/, "");
    if (!q) return null;
    return {
      pred: (i) =>
        i.season.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        i.location.toLowerCase().includes(q) ||
        i.occasion.toLowerCase().includes(q),
      label: q,
    };
  }
  return null;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
